import React from "react";
import { LegalHeader, LegalFooter } from "./Terms";
import useSEO from "../../lib/useSEO";

const SECTIONS = [
  { h: "1. Who we are", p: [
    "Capittance is the operator of the Harmonizer platform at capittance.com. This Privacy Policy explains what personal data we collect, why, and how you can exercise your rights.",
    "Contact: Requests@Capittance.com.",
  ]},
  { h: "2. What we collect", p: [
    "Account data: name, email address, hashed password, role (Operator/CIO/CFO/Admin), subscription plan, verification codes.",
    "Payment data: Stripe customer id, subscription id, payment status, plan and amount. Full card numbers are handled by Stripe and never touch our servers.",
    "Product usage: projects you create, tasks, RAID entries, budget rows, documents, exports, tab-open telemetry, and audit-log entries (actor role + timestamp).",
    "AI conversations: prompts and responses generated by NAVBOT (Claude Sonnet 4.5) — stored so the assistant can maintain multi-turn context.",
    "Connector credentials: for Dynamics 365 and SAP S/4HANA, encrypted secrets used only to fetch your tenant data.",
    "Technical: IP address, browser type, session cookies, and error logs.",
  ]},
  { h: "3. Why we use it (legal basis)", p: [
    "To provide the Service (contract): authentication, subscription enforcement, feature delivery.",
    "To bill you (contract): via Stripe.",
    "To improve the Service (legitimate interests): error logs, usage analytics, product research.",
    "To meet legal obligations: tax records, security logs.",
    "With your consent: marketing communications (opt-in only).",
  ]},
  { h: "4. Who processes your data", p: [
    "Sub-processors we currently use: Stripe (payments), Anthropic (LLM powering NAVBOT — zero-retention posture), Resend (transactional email), MongoDB Atlas (data hosting), and Kubernetes hosting infrastructure.",
    "A current list is available on request at Requests@Capittance.com. We give 30 days\u2019 notice of new sub-processors on request.",
  ]},
  { h: "5. Where your data is stored", p: [
    "Data is stored in Cloud regions selected to meet latency and compliance requirements. Reach out for the current region map if you have residency needs.",
  ]},
  { h: "6. How long we keep it", p: [
    "Active account data: while your subscription is active. Cancelled accounts: 90 days, then hard-deleted unless required for legal reasons (tax, dispute resolution).",
    "Chat history with NAVBOT: retained for the life of the account so multi-turn context works. You can request deletion at any time.",
    "Audit logs: 3 years for security & compliance.",
  ]},
  { h: "7. Your rights (GDPR / CCPA)", p: [
    "You have the right to access, correct, export, or delete your personal data, to restrict or object to processing, and \u2014 for California residents \u2014 the right to know what personal information we collect, the right to delete, the right to opt-out of \u201csale\u201d (we do not sell), and the right to non-discrimination.",
    "To exercise a right, email Requests@Capittance.com from the account\u2019s registered email address. We respond within 30 days.",
  ]},
  { h: "8. Cookies & tracking", p: [
    "We use a small number of first-party cookies for authentication and session state. We use PostHog for product analytics with anonymised session recording. You can opt out via your browser\u2019s do-not-track setting or by emailing us.",
  ]},
  { h: "9. Security", p: [
    "TLS 1.2+ in transit. Passwords hashed with bcrypt. Connector secrets stored server-side only \u2014 never returned to the client. Least-privilege access controls. Full audit trail on data-modifying actions.",
  ]},
  { h: "10. Children", p: [
    "The Service is not directed to individuals under 18. We do not knowingly collect personal information from children.",
  ]},
  { h: "11. Changes to this policy", p: [
    "We\u2019ll notify you of material changes by email or in-app notice at least 30 days before they take effect. The \u201cEffective\u201d date at the top always reflects the current version.",
  ]},
];

export default function Privacy() {
  useSEO({
    title: "Privacy Policy · Capittance Harmonizer data & GDPR handling",
    description: "How Capittance Harmonizer collects, processes and protects your personal data — GDPR & CCPA rights, sub-processors (Stripe, Anthropic, Resend, MongoDB Atlas), retention, and security. Effective 18 February 2026.",
    path: "/privacy",
  });
  return (
    <div className="min-h-screen bg-white">
      <LegalHeader title="Privacy Policy" subtitle="Effective 18 February 2026" />
      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="page-privacy">
        <p className="text-sm text-zinc-500 mb-8 leading-relaxed">
          Plain-English summary of what personal data we handle, why, and your rights over it. If any of this is unclear, email us &mdash; we&rsquo;ll answer within a week.
        </p>
        {SECTIONS.map((s, i) => (
          <section key={i} className="mb-8">
            <h2 className="font-display text-lg font-extrabold tracking-tight text-zinc-900 mb-2">{s.h}</h2>
            {s.p.map((para, j) => <p key={j} className="text-sm text-zinc-700 leading-relaxed mb-2">{para}</p>)}
          </section>
        ))}
        <LegalFooter />
      </main>
    </div>
  );
}
